// Pestaña Dashboard de Costos & BI: tabla del portfolio + resumen de escalabilidad.
// Recibe las variantes YA calculadas por Costs.tsx (costo, PVP sin IVA, margen, ROAS BE);
// acá solo se agrupa por banda y se filtra la tabla. Las bandas son las mismas de siempre:
// ROAS BE ≤2 alta · 2–4 media · >4 baja.
import React, { useMemo, useState } from 'react';
import { ArrowDownRight, Minus, Zap } from 'lucide-react';
import { DataGrid, GridCol } from '../ui/DataGrid';
import { Badge } from '../ui/arc/badge';
import SegmentedControl from '../ui/arc/segmented-control';
import { ars, pct } from './AnalisisUI';

export interface PortfolioRow {
    fullName: string; cost: number; pvp: number; margin: number; marginPercent: number; roasBreakEven: number;
}
type Band = 'high' | 'medium' | 'low';
export const bandOf = (roas: number): Band => (roas <= 2 ? 'high' : roas <= 4 ? 'medium' : 'low');

export const BAND_META: Record<Band, { label: string; tone: 'success' | 'warning' | 'danger'; icon: React.ReactNode; rule: string }> = {
    high: { label: 'Alta', tone: 'success', icon: <Zap size={12} />, rule: 'ROAS BE ≤ 2x' },
    medium: { label: 'Media', tone: 'warning', icon: <Minus size={12} />, rule: 'ROAS BE 2x – 4x' },
    low: { label: 'Baja', tone: 'danger', icon: <ArrowDownRight size={12} />, rule: 'ROAS BE > 4x' },
};

export function bandCounts(rows: PortfolioRow[]) {
    const c: Record<Band, number> = { high: 0, medium: 0, low: 0 };
    rows.forEach(r => { c[bandOf(r.roasBreakEven)]++; });
    return c;
}

export function AnalisisPortfolio({ rows }: { rows: PortfolioRow[] }) {
    const [band, setBand] = useState<'all' | Band>('all');
    const counts = useMemo(() => bandCounts(rows), [rows]);
    // `rows` llega ordenado por margen % (desc); el id fija la clave de cada fila.
    const data = useMemo(() => rows.map((r, i) => ({ ...r, id: i })).filter(r => band === 'all' || bandOf(r.roasBreakEven) === band), [rows, band]);

    const cols: GridCol<PortfolioRow & { id: number }>[] = [
        { key: 'name', header: 'Producto — variante', sort: r => r.fullName, render: r => <span className="block max-w-[28rem] truncate font-medium text-ink" title={r.fullName}>{r.fullName}</span> },
        { key: 'cost', header: 'Costo', align: 'right', sort: r => r.cost, render: r => ars(r.cost), hideOnMobile: true },
        { key: 'pvp', header: 'PVP sin IVA', align: 'right', sort: r => r.pvp, render: r => <span className="font-medium">{ars(r.pvp)}</span> },
        { key: 'margin', header: 'Margen', align: 'right', sort: r => r.margin, render: r => <span className={r.margin > 0 ? 'text-[var(--success)]' : 'text-[var(--danger)]'}>{ars(r.margin)}</span> },
        {
            key: 'mpct', header: 'Margen %', align: 'right', sort: r => r.marginPercent,
            render: r => <Badge size="sm" tone={r.marginPercent >= 30 ? 'success' : r.marginPercent >= 15 ? 'warning' : 'danger'}>{pct(r.marginPercent)}</Badge>,
        },
        { key: 'roas', header: 'ROAS BE', align: 'right', sort: r => r.roasBreakEven, render: r => <span className="font-medium tabular-nums">{r.roasBreakEven.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}x</span> },
        {
            key: 'scale', header: 'Escalabilidad', align: 'center', sort: r => r.roasBreakEven, hideOnMobile: true,
            render: r => { const m = BAND_META[bandOf(r.roasBreakEven)]; return <Badge size="sm" tone={m.tone}><span className="inline-flex items-center gap-1">{m.icon}{m.label}</span></Badge>; },
        },
    ];

    return (
        <DataGrid
            rows={data}
            cols={cols}
            title="Portfolio completo"
            searchPlaceholder="Buscar producto o variante…"
            pageSize={25}
            emptyTitle="Ninguna variante en esta banda"
            filtersActive={band !== 'all'}
            onClearFilters={() => setBand('all')}
            toolbar={
                <SegmentedControl
                    label="Escalabilidad"
                    value={band}
                    onValueChange={v => setBand(v as 'all' | Band)}
                    options={[
                        { value: 'all', label: 'Todas', accessory: <Badge size="sm">{rows.length}</Badge> },
                        { value: 'high', label: 'Alta', accessory: <Badge size="sm" tone="success">{counts.high}</Badge> },
                        { value: 'medium', label: 'Media', accessory: <Badge size="sm" tone="warning">{counts.medium}</Badge> },
                        { value: 'low', label: 'Baja', accessory: <Badge size="sm" tone="danger">{counts.low}</Badge> },
                    ]}
                />
            }
        />
    );
}
